import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Box,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Alert,
  IconButton,
  Chip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import Layout from '../../components/Layout';
import api from '../../services/api';
import { formatDateOnlyForDisplay } from '../../utils/dateOnly';
import {
  addLegacyGallonPriceAliases,
  normalizePricingRow,
  roundPricePerLiterForApi,
  taxPercentInputToDecimal,
} from '../../utils/pricing';

const ManagerPricing = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [pricing, setPricing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPricing, setEditingPricing] = useState(null);
  const [formData, setFormData] = useState({
    date: '',
    diesel_price_per_liter: '',
    def_price_per_liter: '',
    diesel_sales_tax: '',
    def_sales_tax: '',
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    fetchPricing();
  }, []);

  const fetchPricing = async () => {
    try {
      setError('');
      const response = await api.get('/pricing/pricing/');
      const raw = Array.isArray(response.data) ? response.data : response.data?.results || [];
      setPricing(raw.map(normalizePricingRow));
    } catch (error) {
      console.error('Error fetching pricing:', error);
      setError('Failed to load pricing data');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (pricingItem = null) => {
    if (pricingItem) {
      const p = normalizePricingRow(pricingItem);
      setEditingPricing(p);
      // Convert decimal to percentage for display (e.g., 0.0825 -> 8.25)
      const dieselTaxPercent = p.diesel_sales_tax ? (parseFloat(p.diesel_sales_tax) * 100).toFixed(2) : '';
      const defTaxPercent = p.def_sales_tax ? (parseFloat(p.def_sales_tax) * 100).toFixed(2) : '';
      setFormData({
        date: p.date,
        diesel_price_per_liter: p.diesel_price_per_liter ?? '',
        def_price_per_liter: p.def_price_per_liter ?? '',
        diesel_sales_tax: dieselTaxPercent,
        def_sales_tax: defTaxPercent,
      });
    } else {
      setEditingPricing(null);
      const today = new Date().toISOString().split('T')[0];
      setFormData({
        date: today,
        diesel_price_per_liter: '',
        def_price_per_liter: '',
        diesel_sales_tax: '',
        def_sales_tax: '',
      });
    }
    setMessage('');
    setError('');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingPricing(null);
    setFormData({
      date: '',
      diesel_price_per_liter: '',
      def_price_per_liter: '',
      diesel_sales_tax: '',
      def_sales_tax: '',
    });
    setMessage('');
    setError('');
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');
    setError('');

    try {
      if (!String(formData.date || '').trim()) {
        setError('Please choose a date.');
        setSaving(false);
        return;
      }

      const dieselPrice = parseFloat(formData.diesel_price_per_liter);
      const defPrice = parseFloat(formData.def_price_per_liter);
      if (!Number.isFinite(dieselPrice) || !Number.isFinite(defPrice) || dieselPrice <= 0 || defPrice <= 0) {
        setError('Enter valid diesel and DEF prices (numbers greater than 0).');
        setSaving(false);
        return;
      }

      const dieselTax = taxPercentInputToDecimal(formData.diesel_sales_tax);
      const defTax = taxPercentInputToDecimal(formData.def_sales_tax);
      if (!dieselTax.ok || !defTax.ok) {
        setError(dieselTax.ok ? defTax.message : dieselTax.message);
        setSaving(false);
        return;
      }

      const submitData = addLegacyGallonPriceAliases({
        date: formData.date,
        diesel_price_per_liter: roundPricePerLiterForApi(dieselPrice),
        def_price_per_liter: roundPricePerLiterForApi(defPrice),
        diesel_sales_tax: dieselTax.value,
        def_sales_tax: defTax.value,
      });

      if (editingPricing) {
        await api.put(`/pricing/pricing/${editingPricing.id}/`, submitData);
        setMessage('Pricing updated successfully');
      } else {
        await api.post('/pricing/pricing/', submitData);
        setMessage('Pricing created successfully');
      }

      setTimeout(() => {
        handleCloseDialog();
        fetchPricing();
      }, 1000);
    } catch (error) {
      console.error('Error saving pricing:', error.response?.data || error);
      const d = error.response?.data;
      let errorMsg =
        (typeof d?.detail === 'string' && d.detail) ||
        (Array.isArray(d?.detail) && d.detail.join(', ')) ||
        d?.error ||
        d?.message;
      if (!errorMsg && d && typeof d === 'object') {
        errorMsg = Object.values(d)
          .flatMap((v) => (Array.isArray(v) ? v : [v]))
          .filter((x) => x != null && x !== '')
          .join(', ');
      }
      if (!errorMsg) errorMsg = 'Error saving pricing. Please check your input.';
      setError(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (pricingId) => {
    if (!window.confirm('Are you sure you want to delete this pricing record?')) {
      return;
    }

    try {
      await api.delete(`/pricing/pricing/${pricingId}/`);
      setMessage('Pricing deleted successfully');
      fetchPricing();
    } catch (error) {
      console.error('Error deleting pricing:', error);
      setError('Failed to delete pricing');
    }
  };

  const formatTaxRate = (tax) => {
    if (!tax) return '0%';
    return `${(parseFloat(tax) * 100).toFixed(2)}%`;
  };

  if (loading) {
    return (
      <Layout>
        <Container>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
            <CircularProgress />
          </Box>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4">Pricing Management</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Pricing
          </Button>
        </Box>

        {message && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setMessage('')}
          >
            {message}
          </Alert>
        )}

        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={() => setError('')}
          >
            {error}
          </Alert>
        )}

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Diesel $/L</TableCell>
                <TableCell>Diesel Sales Tax</TableCell>
                <TableCell>DEF $/L</TableCell>
                <TableCell>DEF Sales Tax</TableCell>
                <TableCell>Created By</TableCell>
                <TableCell>Created At</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pricing.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="textSecondary">
                      No pricing records found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                pricing.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>{formatDateOnlyForDisplay(item.date)}</TableCell>
                    <TableCell>${parseFloat(item.diesel_price_per_liter).toFixed(2)}</TableCell>
                    <TableCell>
                      <Chip
                        label={formatTaxRate(item.diesel_sales_tax)}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>${parseFloat(item.def_price_per_liter).toFixed(2)}</TableCell>
                    <TableCell>
                      <Chip
                        label={formatTaxRate(item.def_sales_tax)}
                        size="small"
                        color="secondary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{item.created_by_name || 'N/A'}</TableCell>
                    <TableCell>{new Date(item.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(item)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(item.id)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Create/Edit Dialog */}
        <Dialog 
          open={openDialog} 
          onClose={handleCloseDialog} 
          maxWidth="sm" 
          fullWidth
          fullScreen={isMobile}
          sx={{
            '& .MuiDialog-paper': {
              m: { xs: 0, sm: 2 },
            },
          }}
        >
          <DialogTitle>
            {editingPricing ? 'Edit Pricing' : 'Add New Pricing'}
          </DialogTitle>
          <form onSubmit={handleSubmit}>
            <DialogContent dividers>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                  {error}
                </Alert>
              )}
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Date"
                    name="date"
                    type="date"
                    value={formData.date}
                    onChange={handleChange}
                    required
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Diesel price per liter ($)"
                    name="diesel_price_per_liter"
                    type="number"
                    value={formData.diesel_price_per_liter}
                    onChange={handleChange}
                    required
                    inputProps={{ step: '0.01', min: '0' }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Diesel Sales Tax (%)"
                    name="diesel_sales_tax"
                    type="number"
                    value={formData.diesel_sales_tax}
                    onChange={handleChange}
                    helperText="Enter as percentage (e.g., 8.25 for 8.25%) - max 4 decimal places"
                    inputProps={{ step: '0.0001', min: '0', max: '100' }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="DEF price per liter ($)"
                    name="def_price_per_liter"
                    type="number"
                    value={formData.def_price_per_liter}
                    onChange={handleChange}
                    required
                    inputProps={{ step: '0.01', min: '0' }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="DEF Sales Tax (%)"
                    name="def_sales_tax"
                    type="number"
                    value={formData.def_sales_tax}
                    onChange={handleChange}
                    helperText="Enter as percentage (e.g., 8.25 for 8.25%) - max 4 decimal places"
                    inputProps={{ step: '0.0001', min: '0', max: '100' }}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog} disabled={saving}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" disabled={saving}>
                {saving ? 'Saving...' : editingPricing ? 'Update' : 'Create'}
              </Button>
            </DialogActions>
          </form>
        </Dialog>
      </Container>
    </Layout>
  );
};

export default ManagerPricing;

